'use client'

import { useTranslations } from 'next-intl'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  SCALE_OPTIONS,
  toggleChartScaleOption,
  type ToolbarPriceScaleMode,
} from '@/features/chart/lib/utils/chart-util'
import { useWidgetConfigSync } from '@/features/dashboard/hooks/use-widget-config-sync'
import { useLinkingGroupState } from '@/hooks/use-linking-group-state'
import { cn } from '@/lib/utils'

/**
 * The % / Log / Auto price-scale toggles in the Percept chart's footer (TV uses its own native
 * bottom-toolbar buttons). Both engines read the same `chartOption.scale` / `autoScale`, so
 * switching engines keeps the scale.
 */
export default function ChartScaleControls({
  cardId,
  isMobileUI,
  syncExtra,
}: {
  cardId: string
  isMobileUI: boolean
  /** Fields written alongside the scale (the TV toolbar also sends timeframe and chartType). */
  syncExtra?: Record<string, string>
}) {
  const t = useTranslations('trade.chartToolbar')
  const { chartOption, setChartOption } = useLinkingGroupState(cardId)
  const { syncConfig } = useWidgetConfigSync()
  const selectedScale = SCALE_OPTIONS.find(
    (option) => option.value === (chartOption?.scale ?? 'Normal')
  )
  const isAutoScale = chartOption?.autoScale ?? true
  const mobileScaleLabel = [
    selectedScale && selectedScale.value !== 'Normal'
      ? t(`scale.${selectedScale.value}`)
      : undefined,
    isAutoScale ? t('scale.Normal') : undefined,
  ]
    .filter(Boolean)
    .join(' · ')

  const onClickScale = (value: ToolbarPriceScaleMode) => {
    const { scale, autoScale } = toggleChartScaleOption(
      chartOption?.scale ?? 'Normal',
      isAutoScale,
      value
    )
    setChartOption((prev) => ({ ...prev, scale, autoScale }))
    syncConfig(cardId, { ...syncExtra, scale, autoScale: String(autoScale) })
  }

  const isActive = (value: ToolbarPriceScaleMode) =>
    value === 'Normal' ? isAutoScale : selectedScale?.value === value

  if (isMobileUI) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button className="text-foreground/80 hover:text-foreground flex h-[15px] items-center px-1.5 text-[11px]">
            {mobileScaleLabel || t('scale.Off')}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-20 p-1" side="top" align="end">
          <div className="flex flex-col gap-1">
            {SCALE_OPTIONS.map((option) => (
              <button
                key={option.value}
                className={cn(
                  'hover:bg-foreground/10 flex w-full items-center px-3 py-2 text-xs transition-colors',
                  isActive(option.value) ? 'text-foreground font-medium' : 'text-white/70'
                )}
                onClick={() => onClickScale(option.value)}
              >
                {t(`scale.${option.value}`)}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <div className="text-foreground/60 flex shrink-0 items-center gap-1 text-[11px] tracking-tight">
      {SCALE_OPTIONS.map((option) => (
        <button
          className={cn(
            'flex h-[15px] cursor-pointer items-center justify-center px-1 text-center font-sans text-[11px] tracking-tight transition-colors disabled:cursor-default',
            isActive(option.value) ? 'text-foreground font-medium' : 'hover:text-foreground/80'
          )}
          onClick={() => onClickScale(option.value)}
          key={option.value}
        >
          {t(`scale.${option.value}`)}
        </button>
      ))}
    </div>
  )
}
